import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ConsumerLayout from './ConsumerLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { 
  Store, Search, MapPin, Clock, ShoppingCart, 
  ChevronRight, Loader2, Tag
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';

export default function StoreDirectory({ user, onLogout }) {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    loadStores();
  }, []);

  const loadStores = async () => {
    try {
      setLoading(true);
      const response = await api.get('/store/directory');
      setStores(response.data.stores || []);
    } catch (error) {
      console.error('Failed to load stores:', error);
      toast.error('Failed to load stores');
    } finally {
      setLoading(false);
    }
  };

  const filteredStores = stores.filter(store => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      store.name?.toLowerCase().includes(query) ||
      store.store_name?.toLowerCase().includes(query) ||
      store.address?.toLowerCase().includes(query)
    );
  });

  const handleStoreClick = (store) => {
    navigate(`/store/${store.slug}/storefront`);
  };

  // Group stores by those with deals vs without
  const storesWithDeals = filteredStores.filter(s => s.deal_count > 0);
  const storesWithoutDeals = filteredStores.filter(s => s.deal_count === 0);

  return (
    <ConsumerLayout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>
            Stores
          </h1>
          <p className="text-gray-600 mt-1">Browse deals from local grocery retailers</p>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            placeholder="Search stores..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="store-search"
          />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
          </div>
        ) : (
          <>
            {/* Stores with Active Deals */}
            {storesWithDeals.length > 0 && (
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  <Tag className="w-5 h-5 text-emerald-600" />
                  Stores with Deals
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                  {storesWithDeals.map((store) => (
                    <StoreCard 
                      key={store.id} 
                      store={store} 
                      onClick={() => handleStoreClick(store)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* All Stores / Stores without Deals */}
            {storesWithoutDeals.length > 0 && (
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-gray-700">
                  {storesWithDeals.length > 0 ? 'Other Stores' : 'All Stores'}
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                  {storesWithoutDeals.map((store) => (
                    <StoreCard 
                      key={store.id} 
                      store={store} 
                      onClick={() => handleStoreClick(store)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Empty State */}
            {filteredStores.length === 0 && (
              <Card>
                <CardContent className="py-12 text-center">
                  <Store className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Stores Found</h3>
                  <p className="text-gray-500">
                    {searchQuery 
                      ? `No stores match "${searchQuery}"`
                      : 'No stores are currently available in your area'
                    }
                  </p>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </ConsumerLayout>
  );
}

function StoreCard({ store, onClick }) {
  const hasDeals = store.deal_count > 0;
  
  return (
    <button
      onClick={onClick}
      className="group flex flex-col items-center p-4 bg-white rounded-xl border border-gray-200 hover:border-emerald-400 hover:shadow-lg transition-all text-center"
      data-testid={`store-card-${store.slug}`}
    >
      {/* Logo/Icon with Deal Badge */}
      <div className="relative mb-3">
        <div className={`w-16 h-16 rounded-xl flex items-center justify-center ${
          hasDeals ? 'bg-emerald-100' : 'bg-gray-100'
        }`}>
          {store.logo_url ? (
            <img 
              src={store.logo_url} 
              alt={store.store_name || store.name}
              className="w-12 h-12 object-contain rounded-lg"
            />
          ) : (
            <Store className={`w-8 h-8 ${hasDeals ? 'text-emerald-600' : 'text-gray-400'}`} />
          )}
        </div>
        
        {/* Deal Count Badge */}
        {hasDeals && (
          <div className="absolute -top-2 -right-2 bg-emerald-600 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center shadow-md">
            <ShoppingCart className="w-3 h-3 mr-0.5" />
            <span className="text-[10px]">{store.deal_count}</span>
          </div>
        )}
      </div>

      {/* Store Name */}
      <h3 className="font-semibold text-gray-900 text-sm leading-tight mb-1 group-hover:text-emerald-600 transition-colors line-clamp-2">
        {store.store_name || store.name}
      </h3>

      {/* Store Info */}
      {store.hours && (
        <p className="text-xs text-gray-500 flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {store.hours}
        </p>
      )}

      {/* Deal Status */}
      {hasDeals ? (
        <Badge className="mt-2 bg-emerald-100 text-emerald-700 text-xs">
          {store.deal_count} {store.deal_count === 1 ? 'Deal' : 'Deals'}
        </Badge>
      ) : (
        <span className="mt-2 text-xs text-gray-400">No deals yet</span>
      )}
    </button>
  );
}
